<template>
    <div class="hero-profile-card">
        <MaltImg :src="photoUrl" :alt="photoAlt" :style="{objectPosition: photoPosition}" class="hero-profile-card__photo" />
        <div class="hero-profile-card__body">
            <span class="hero-profile-card__name">{{ name }}</span>
            <span class="hero-profile-card__job-title" data-testid="hero-profile-card-job-title">
                {{ jobTitle }}
                <template v-if="company">
                    <span>-</span>
                    <span class="hero-profile-card__company" data-testid="hero-profile-card-company">
                        <VJoyIcon
                            name="company-placeholder"
                            size="xsmall"
                            color="neutral-50"
                            aria-hidden="true"
                            data-testid="hero-profile-card-company-icon"
                        />
                        {{ company }}
                    </span>
                </template>
            </span>
            <span v-if="review" class="hero-profile-card__review">{{ review }}</span>
            <span class="hero-profile-card__rating">
                <span class="hero-profile-card__stars">
                    <VJoyIcon v-for="star in RATING_STARS" :key="star" name="star-fill" color="quaternary" size="xxsmall" />
                </span>
                <span class="hero-profile-card__score">{{ score }}</span>
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {MaltImg} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    interface Props {
        jobTitle: string;
        company?: string;
        name: string;
        photoPosition: string;
        photoUrl: string;
        photoAlt: string;
        review: string;
        score: string;
    }

    defineProps<Props>();

    const RATING_STARS = [1, 2, 3, 4, 5];
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .hero-profile-card {
        display: flex;
        align-items: center;
        gap: var(--joy-core-spacing-2);
        padding: var(--joy-core-spacing-2);
        border-radius: 20px;
        background-color: color-mix(in srgb, var(--joy-color-neutral-0) 90%, transparent);
        backdrop-filter: blur(20px);
        box-shadow: 0 26px 60px rgb(20 8 8 / 30%);
        width: 320px;

        @media screen and (min-width: 1350px) {
            width: 350px;
        }

        @media screen and (min-width: 1450px) {
            width: 400px;
            padding: var(--joy-core-spacing-3);
            gap: var(--joy-core-spacing-4);
        }

        &__photo {
            display: block;
            flex: none;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            object-fit: cover;

            @media screen and (min-width: 1450px) {
                width: 70px;
                height: 70px;
            }
        }

        &__body {
            display: flex;
            min-width: 0;
            flex-direction: column;
            gap: var(--joy-core-spacing-0);

            @media screen and (min-width: 1450px) {
                gap: var(--joy-core-spacing-0);
            }
        }

        &__name {
            @include text.getFontBySize(primary-300);

            font-weight: var(--joy-font-weight-bold);
            letter-spacing: -0.16px;

            @media screen and (min-width: 1450px) {
                @include text.getFontBySize(primary-400);

                font-weight: var(--joy-font-weight-bold);
            }
        }

        &__job-title {
            @include text.getFontBySize(primary-200);

            gap: var(--joy-core-spacing-1);
            color: var(--joy-color-neutral-40);
            white-space: nowrap;
            display: inline-flex;
            align-items: center;

            @media screen and (min-width: 1450px) {
                @include text.getFontBySize(primary-300);
            }
        }

        &__company {
            display: inline-flex;
            align-items: center;
            gap: var(--joy-core-spacing-1);
        }

        &__review {
            overflow: hidden;
            color: var(--joy-color-neutral-40);
            font-size: var(--joy-font-size-primary-200);
            text-overflow: ellipsis;
            white-space: nowrap;

            @media screen and (min-width: 1450px) {
                font-size: var(--joy-font-size-primary-300);
            }
        }

        &__rating {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);
        }

        &__score {
            font-size: var(--joy-font-size-primary-200);
            font-weight: 600;
        }
    }
</style>
